import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { Badge, Card, Dropdown, ListGroup, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import FalconCardHeader from 'components/common/FalconCardHeader';
import { useAuth } from 'context/AuthContext';
import {
  fetchMessagesSummary,
  getCachedMessagesSummary,
  subscribeMessagesSummary
} from 'helpers/messages';

const formatMessageTime = value => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString();
};

const getConversationRoute = conversation => (
  conversation?.source === 'mercadolibre'
    ? `/mercadolibre-messages?conversation=${conversation.id}&source=${conversation.source}`
    : `/messages?conversation=${conversation.id}&source=${conversation.source}`
);

const MessageDropdown = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [summary, setSummary] = useState({
    conversations: [],
    unread_count: 0,
    unread_by_source: {}
  });
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const unreadCount = Number(summary.unread_count || 0);
  const hasUnread = unreadCount > 0;
  const defaultMessagesRoute = hasPermission('messages.view_internal')
    ? '/messages'
    : '/mercadolibre-messages';

  const applySummary = nextSummary => {
    const data = nextSummary || {};
    setSummary({
      conversations: data.conversations || [],
      unread_count: Number(data.unread_count || 0),
      unread_by_source: data.unread_by_source || {}
    });
  };

  const loadSummary = async options => {
    const silent = Boolean(options && options.silent);
    const force = Boolean(options && options.force);
    if (!silent) {
      setLoading(true);
    }

    try {
      const data = await fetchMessagesSummary({ force });
      const nextSummary = {
        conversations: data?.conversations || [],
        unread_count: Number(data?.unread_count || 0),
        unread_by_source: data?.unread_by_source || {}
      };
      applySummary(nextSummary);
      return nextSummary;
    } catch (error) {
      console.error('Error loading messages summary:', error);
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const cachedSummary = getCachedMessagesSummary();
    if (cachedSummary) {
      applySummary(cachedSummary);
    }

    const unsubscribe = subscribeMessagesSummary(nextSummary => {
      applySummary(nextSummary);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    loadSummary({ silent: true, force: true });
    return undefined;
  }, [isOpen]);

  const handleToggle = nextOpenState => {
    setIsOpen(nextOpenState);
  };

  const recentConversations = useMemo(
    () => (summary.conversations || []).slice(0, 8),
    [summary.conversations]
  );

  return (
    <Dropdown navbar={true} as="li" show={isOpen} onToggle={handleToggle}>
      <Dropdown.Toggle
        bsPrefix="toggle"
        as={Link}
        to="#!"
        className={classNames('px-2 nav-link', {
          'notification-indicator notification-indicator-primary': hasUnread
        })}
      >
        <FontAwesomeIcon icon="comments" transform="shrink-6" className="fs-4" />
        {hasUnread ? (
          <span className="notification-indicator-number">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </Dropdown.Toggle>

      <Dropdown.Menu className="dropdown-menu-card dropdown-menu-end dropdown-caret dropdown-caret-bg">
        <Card
          className="dropdown-menu-notification dropdown-menu-end shadow-none"
          style={{ maxWidth: '22rem' }}
        >
          <FalconCardHeader
            className="card-header"
            title={t('MESSAGES.DROPDOWN.TITLE')}
            titleTag="h6"
            light={false}
          />
          <ListGroup
            variant="flush"
            className="fw-normal fs--1 scrollbar"
            style={{ maxHeight: '22rem' }}
          >
            {loading ? (
              <div className="py-4 text-center">
                <Spinner animation="border" size="sm" className="me-2" />
                {t('MESSAGES.DROPDOWN.LOADING')}
              </div>
            ) : recentConversations.length ? (
              recentConversations.map(conversation => (
                <ListGroup.Item key={conversation.id} className="p-0">
                  <Link
                    className={classNames('notification notification-flush', {
                      'notification-unread': conversation.unread
                    })}
                    to={getConversationRoute(conversation)}
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="notification-body">
                      <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                        <span className="fw-semi-bold text-900">
                          {conversation.title}
                        </span>
                        <Badge
                          pill
                          bg={conversation.source === 'mercadolibre' ? 'warning' : 'info'}
                        >
                          {t(`MESSAGES.SOURCES.${conversation.source.toUpperCase()}`)}
                        </Badge>
                        {conversation.is_group ? (
                          <Badge pill bg="secondary">
                            {t('MESSAGES.CONVERSATIONS.GROUP')}
                          </Badge>
                        ) : null}
                      </div>
                      <div className="text-600 fs--1">
                        {conversation.last_message_preview || t('MESSAGES.CONVERSATIONS.NO_MESSAGES')}
                      </div>
                      <span className="notification-time">
                        {formatMessageTime(conversation.last_message_at)}
                      </span>
                    </div>
                  </Link>
                </ListGroup.Item>
              ))
            ) : (
              <div className="py-4 px-3 text-center text-600">
                {t('MESSAGES.DROPDOWN.EMPTY')}
              </div>
            )}
          </ListGroup>
          <div
            className="card-footer text-center border-top"
            onClick={() => setIsOpen(false)}
          >
            <Link className="card-link d-block" to={defaultMessagesRoute}>
              {t('MESSAGES.ACTIONS.VIEW_ALL')}
            </Link>
          </div>
        </Card>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default MessageDropdown;
